import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Target, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function statusColor(status) {
  if (status === "over") return "bg-[#E63946]";
  if (status === "warn") return "bg-[#E07A5F]";
  return "bg-[#4C956C]";
}

export default function Budgets() {
  const { user } = useAuth();
  const currency = user?.currency || "USD";
  const [progress, setProgress] = useState({ month: "", budgets: [] });
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [limit, setLimit] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    const [{ data: prog }, { data: cats }] = await Promise.all([
      api.get("/budgets/progress"),
      api.get("/categories"),
    ]);
    setProgress(prog);
    setCategories(cats.expense || []);
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/budgets", {
        category, monthly_limit: Number(limit), currency,
      });
      toast.success("Budget added");
      setOpen(false); setCategory(""); setLimit("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add budget");
    }
  };

  const remove = async () => {
    if (!deleteId) return;
    await api.delete(`/budgets/${deleteId}`);
    toast.success("Budget removed");
    setDeleteId(null);
    load();
  };

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Guardrails</div>
          <h1 className="font-display text-4xl mt-2">Budgets</h1>
          <p className="text-sm text-[var(--muted-text)] mt-1">Set monthly caps per expense category. Track progress in real time.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-budget-btn" className="rounded-full bg-[#E07A5F] hover:bg-[#c9694f] text-white h-11 px-5">
              <Plus className="w-4 h-4 mr-2" /> New budget
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Create budget</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="budget-category"><SelectValue placeholder="Choose a category" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.name} data-testid={`budget-cat-${c.name}`}>
                        <span className="inline-flex items-center gap-2"><span style={{ color: c.color }}>{c.icon}</span>{c.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Monthly limit</Label>
                <Input
                  data-testid="budget-limit"
                  type="number" step="0.01" min="0.01" required
                  value={limit} onChange={(e) => setLimit(e.target.value)}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button
                  data-testid="budget-submit"
                  type="submit"
                  disabled={!category || !limit}
                  className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]"
                >
                  Add budget
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)] mb-4">Current month · {progress.month}</div>

      {progress.budgets.length === 0 ? (
        <div data-testid="budgets-empty" className="card-flat p-12 text-center">
          <Target className="w-8 h-8 mx-auto text-[var(--muted-text)]" />
          <h3 className="font-display text-xl mt-3">No budgets yet</h3>
          <p className="text-sm text-[var(--muted-text)] mt-1">Create one to get overspending alerts.</p>
        </div>
      ) : (
        <div data-testid="budgets-list" className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {progress.budgets.map((b) => (
            <div key={b.id} data-testid={`budget-row-${b.id}`} className="card-flat p-6 hover-lift">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">{b.currency}</div>
                  <h3 className="font-display text-xl mt-1">{b.category}</h3>
                </div>
                <div className="flex items-center gap-2">
                  {b.status === "warn" && <AlertTriangle className="w-4 h-4 text-[#E07A5F]" />}
                  {b.status === "over" && <AlertTriangle className="w-4 h-4 text-[#E63946]" />}
                  <Button
                    data-testid={`budget-delete-${b.id}`}
                    size="icon" variant="ghost"
                    onClick={() => setDeleteId(b.id)}
                  >
                    <Trash2 className="w-4 h-4 text-[#E63946]" />
                  </Button>
                </div>
              </div>

              <div className="mt-4 flex items-baseline justify-between">
                <div className="font-mono-data text-2xl font-semibold">{formatCurrency(b.spent, b.currency)}</div>
                <div className="text-sm text-[var(--muted-text)]">/ {formatCurrency(b.monthly_limit, b.currency)}</div>
              </div>

              <div className="mt-3 h-2 rounded-full overflow-hidden bg-[var(--track)]">
                <div
                  className={`h-full ${statusColor(b.status)} transition-all duration-300`}
                  style={{ width: `${Math.min(b.percent, 100)}%` }}
                />
              </div>
              <div className={`mt-2 text-xs ${
                b.status === "over" ? "text-[#E63946]" :
                b.status === "warn" ? "text-[#E07A5F]" : "text-[var(--muted-text)]"
              }`}>
                {b.percent}% used {b.status === "over" && "· over budget"}
                {b.status === "warn" && "· getting close"}
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete budget?</AlertDialogTitle>
            <AlertDialogDescription>You&apos;ll stop getting alerts for this category.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="budget-delete-confirm"
              onClick={remove}
              className="bg-[#E63946] hover:bg-[#c92e3b] text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
